/**
 * Community systems that live on the battle server: fan mail (stickers for
 * the artist of a monster you fought), dares between friends and the boss
 * of the week. The server keeps the shared state; this file turns it into
 * notebook monsters and local rewards.
 */
import type { BossResponse, DareDTO, DareNote, FightKind, FightResult, Reaction } from '@shared/protocol/messages';
import { validateMonster } from '../combat/budget';
import { t } from '../../i18n';
import { monsterFromDTO } from './monsters';
import type { Memory, Monster, SaveData } from './model';
import { addMemory } from './progression';

/** Fixed stickers only: no typed text, so it is safe in kid mode. */
export const REACTION_INFO: Record<Reaction, { label: string; sticker: NonNullable<Memory['sticker']> }> = {
  cute: { label: 'Cute!', sticker: 'heart' },
  scary: { label: 'Scary!', sticker: 'skull' },
  funny: { label: 'So funny!', sticker: 'pencil' },
  cool: { label: 'Cool!', sticker: 'star' },
  tough: { label: 'Too tough!', sticker: 'crown' },
};

/** The ready-made notes a dare can carry. */
export const DARE_NOTE_TEXT: Record<DareNote, string> = {
  beat_me: 'Beat me if you can!',
  too_easy: 'Bet this one is too easy for you.',
  new_drawing: 'I drew something new. Try it!',
  my_boss: 'Meet my boss monster!',
  good_luck: 'Good luck. You will need it!',
};

/** Coins for everyone who helped when the boss of the week falls. */
export const BOSS_PRIZE_COINS = 40;
/** Boss weeks remembered for the prize (older ones can never come back). */
const KEEP_BOSS_WEEKS = 12;

export const dareMonsterId = (dareId: string): string => `dare:${dareId}`;
export const bossMonsterId = (round: string): string => `boss:${round}`;

/** What to tell the server after a battle against this monster (null = nothing, it only lives here). */
export type FightTarget = { kind: FightKind; ref: string } | { kind: 'boss'; round: string; entryId: string };

export function fightTarget(m: Monster): FightTarget | null {
  switch (m.source) {
    case 'shared':
      return m.shareCode ? { kind: 'share', ref: m.shareCode } : null;
    case 'hall':
      return { kind: 'hall', ref: m.id.slice('hall:'.length) };
    case 'dare':
      return { kind: 'dare', ref: m.id.slice('dare:'.length) };
    case 'boss':
      return m.shareCode ? { kind: 'boss', round: m.id.slice('boss:'.length), entryId: m.shareCode } : null;
    default:
      return null;
  }
}

export const fightResult = (outcome: 'me' | 'them' | 'draw'): FightResult => (outcome === 'me' ? 'win' : outcome === 'them' ? 'loss' : 'draw');

/** Keeps a server monster's win/loss record when it is refreshed. */
function upsert(save: SaveData, fresh: Monster): Monster {
  const old = save.monsters.find((m) => m.id === fresh.id);
  if (!old) {
    save.monsters.push(fresh);
    return fresh;
  }
  Object.assign(old, { ...fresh, wins: old.wins, losses: old.losses, createdAt: old.createdAt });
  return old;
}

/**
 * Tapes every dare friends sent me into the notebook (and takes out the ones
 * that were taken back or put away). Dares that fail the budget check are skipped.
 */
export function syncDares(save: SaveData, incoming: readonly DareDTO[]): Monster[] {
  const out: Monster[] = [];
  const keep = new Set<string>();
  for (const d of incoming) {
    if (!validateMonster(d.monster).ok) continue;
    const fresh = monsterFromDTO(d.monster, 'dare', d.fromName);
    fresh.id = dareMonsterId(d.id);
    fresh.createdAt = Date.parse(d.createdAt) || Date.now();
    keep.add(fresh.id);
    out.push(upsert(save, fresh));
  }
  save.monsters = save.monsters.filter((m) => m.source !== 'dare' || keep.has(m.id));
  return out;
}

/** Takes one dare out of the notebook (put away, or taken back by the sender). */
export function dropDare(save: SaveData, dareId: string): void {
  save.monsters = save.monsters.filter((m) => m.id !== dareMonsterId(dareId));
}

/**
 * Puts this week's boss into the notebook and removes older weeks' bosses.
 * The boss keeps its contest entry id in shareCode, for the fan mail.
 */
export function syncBoss(save: SaveData, res: BossResponse): Monster | null {
  const id = bossMonsterId(res.round);
  save.monsters = save.monsters.filter((m) => m.source !== 'boss' || (m.id === id && res.boss !== null));
  if (!res.boss) return null;
  const fresh = monsterFromDTO(res.boss.monster, 'boss', res.boss.authorName, res.boss.entryId);
  fresh.id = id;
  return upsert(save, fresh);
}

/** Gives the "we beat the boss" prize once per week to a player who helped. Returns the coins. */
export function claimBossPrize(save: SaveData, res: BossResponse): number {
  const b = res.boss;
  if (!b?.defeated || b.myHits === 0 || save.community.bossPrizes.includes(res.round)) return 0;
  save.community.bossPrizes.push(res.round);
  if (save.community.bossPrizes.length > KEEP_BOSS_WEEKS) save.community.bossPrizes.splice(0, save.community.bossPrizes.length - KEEP_BOSS_WEEKS);
  save.coins += BOSS_PRIZE_COINS;
  addMemory(save, {
    kind: 'quest',
    title: t('Everyone beat the boss: {monster}!', { monster: b.monster.name }),
    text: t('{n} players knocked it down together. +{coins} coins for helping.', { n: b.fighters, coins: BOSS_PRIZE_COINS }),
    sticker: 'crown',
  });
  return BOSS_PRIZE_COINS;
}

/** Damage dealt to a monster in one battle (its max HP minus what it has left). */
export function damageDealt(maxHp: number, hpLeft: number): number {
  return Math.max(0, Math.min(maxHp, maxHp - Math.max(0, hpLeft)));
}

/** Notebook memory for a sticker I sent. */
export function noteReaction(save: SaveData, monster: Monster, reaction: Reaction): void {
  save.counters.reactionsSent++;
  addMemory(save, {
    kind: 'friend',
    title: t('Sent "{sticker}" to {name}', { sticker: t(REACTION_INFO[reaction].label), name: monster.author ?? t('a friend') }),
    text: t('For {monster}.', { monster: monster.name }),
    sticker: REACTION_INFO[reaction].sticker,
  });
}
